import { useEffect, useRef, useCallback } from "react";
import {
  Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, Quote, Minus,
  AlignLeft, AlignCenter, AlignRight,
  Link, Image, Undo, Redo, Heading1, Heading2, Heading3,
} from "lucide-react";

// ── helpers ──────────────────────────────────────────────────────
const exec = (cmd, value = null) => document.execCommand(cmd, false, value);

const ToolbarBtn = ({ onClick, title, active, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`p-1.5 rounded-lg transition text-sm ${
      active
        ? "bg-orange-500 text-white"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-5 bg-gray-200 mx-1" />;

// ── main component ───────────────────────────────────────────────
const RichEditor = ({ value, onChange, placeholder = "Write your content here..." }) => {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // Sync external value → DOM only on mount or external reset
  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== value) {
      isInternalChange.current = true;
      editorRef.current.innerHTML = value || "";
      isInternalChange.current = false;
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!isInternalChange.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const insertLink = () => {
    const url = window.prompt("Enter URL:", "https://");
    if (url) exec("createLink", url);
  };

  const insertImage = () => {
    const url = window.prompt("Enter image URL:", "https://");
    if (url) exec("insertImage", url);
  };

  const insertHR = () => exec("insertHTML", "<hr/>");

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500 transition">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-gray-100 bg-gray-50">

        {/* History */}
        <ToolbarBtn title="Undo" onClick={() => exec("undo")}><Undo size={15} /></ToolbarBtn>
        <ToolbarBtn title="Redo" onClick={() => exec("redo")}><Redo size={15} /></ToolbarBtn>
        <Divider />

        {/* Headings */}
        <ToolbarBtn title="Heading 1" onClick={() => exec("formatBlock", "<h1>")}><Heading1 size={15} /></ToolbarBtn>
        <ToolbarBtn title="Heading 2" onClick={() => exec("formatBlock", "<h2>")}><Heading2 size={15} /></ToolbarBtn>
        <ToolbarBtn title="Heading 3" onClick={() => exec("formatBlock", "<h3>")}><Heading3 size={15} /></ToolbarBtn>
        <Divider />

        {/* Inline styles */}
        <ToolbarBtn title="Bold (Ctrl+B)"      onClick={() => exec("bold")}         ><Bold          size={15} /></ToolbarBtn>
        <ToolbarBtn title="Italic (Ctrl+I)"    onClick={() => exec("italic")}       ><Italic        size={15} /></ToolbarBtn>
        <ToolbarBtn title="Underline (Ctrl+U)" onClick={() => exec("underline")}    ><Underline     size={15} /></ToolbarBtn>
        <ToolbarBtn title="Strikethrough"      onClick={() => exec("strikeThrough")}><Strikethrough size={15} /></ToolbarBtn>
        <Divider />

        {/* Alignment */}
        <ToolbarBtn title="Align Left"   onClick={() => exec("justifyLeft")}  ><AlignLeft   size={15} /></ToolbarBtn>
        <ToolbarBtn title="Align Center" onClick={() => exec("justifyCenter")}><AlignCenter size={15} /></ToolbarBtn>
        <ToolbarBtn title="Align Right"  onClick={() => exec("justifyRight")} ><AlignRight  size={15} /></ToolbarBtn>
        <Divider />

        {/* Lists */}
        <ToolbarBtn title="Bullet List"  onClick={() => exec("insertUnorderedList")}><List        size={15} /></ToolbarBtn>
        <ToolbarBtn title="Number List"  onClick={() => exec("insertOrderedList")}  ><ListOrdered size={15} /></ToolbarBtn>
        <Divider />

        {/* Block */}
        <ToolbarBtn title="Blockquote"    onClick={() => exec("formatBlock", "<blockquote>")}><Quote  size={15} /></ToolbarBtn>
        <ToolbarBtn title="Divider line"  onClick={insertHR}                                 ><Minus  size={15} /></ToolbarBtn>
        <Divider />

        {/* Media */}
        <ToolbarBtn title="Insert Link"  onClick={insertLink} ><Link  size={15} /></ToolbarBtn>
        <ToolbarBtn title="Insert Image" onClick={insertImage}><Image size={15} /></ToolbarBtn>

      </div>

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        className={`
          min-h-[260px] max-h-[480px] overflow-y-auto p-4 text-sm text-gray-700 outline-none
          prose prose-sm max-w-none
          [&_h1]:text-2xl [&_h1]:font-extrabold [&_h1]:text-gray-900 [&_h1]:mt-4 [&_h1]:mb-2
          [&_h2]:text-xl  [&_h2]:font-bold      [&_h2]:text-gray-900 [&_h2]:mt-4 [&_h2]:mb-2
          [&_h3]:text-lg  [&_h3]:font-bold      [&_h3]:text-gray-800 [&_h3]:mt-3 [&_h3]:mb-1
          [&_p]:my-1.5 [&_p]:leading-relaxed
          [&_strong]:font-bold
          [&_em]:italic
          [&_u]:underline
          [&_ul]:list-disc   [&_ul]:pl-5 [&_ul]:my-2
          [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
          [&_li]:my-0.5
          [&_blockquote]:border-l-4 [&_blockquote]:border-orange-400 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-500 [&_blockquote]:my-3
          [&_a]:text-orange-500 [&_a]:underline
          [&_img]:rounded-xl [&_img]:max-w-full [&_img]:my-3
          [&_hr]:border-gray-200 [&_hr]:my-4
        `}
        data-placeholder={placeholder}
        style={{ wordBreak: "break-word" }}
      />

      {/* Placeholder via CSS */}
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default RichEditor;
